import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useCurrentDomain } from "../context/DomainContext";
import { assignCompanySetting, getSettingDropdowns } from "../services/masterDataService";
import { THEME } from "../theme/themeColors";

const SPECIAL_KEY_MAP = {
  SS00003: "extddlUserLawFirm",
  SS00012: "extdllUserName",
  SS00013: "extddlTreatmentAddress",
  SS0139: "extddlTreatmentAddress",
  SS00010: "extdlPOS",
  SS0142: "extddlProviderQualifier",
  SS00060: "extddlVisitType",
  SS0154: "extddlNF3Template",
};

const SettingAssignModal = ({
  setting,
  companyId,
  existingSetting,
  onClose,
  onSaved,
}) => {
  const { domain } = useCurrentDomain();
  const [saving, setSaving] = useState(false);
  const [dropdowns, setDropdowns] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);

  const settingKeyId = setting?.settingKeyId || setting?.SettingKeyId;
  const isSpecial = !!SPECIAL_KEY_MAP[settingKeyId];
  const hasSubValue = (setting?.hasSubValue || setting?.HasSubValue) && !isSpecial;

  const [value, setValue] = useState(existingSetting?.settingValue || "");
  const [subValue, setSubValue] = useState(existingSetting?.subSettingValue || "AM");
  const [enabled, setEnabled] = useState(existingSetting?.settingValue !== "0");

  useEffect(() => {
    if (!isSpecial || !domain || !companyId) return;

    setLoadingDropdowns(true);
    getSettingDropdowns(domain, companyId)
      .then((data) => setDropdowns(data || []))
      .catch(() => toast.error("Failed to load dropdown options"))
      .finally(() => setLoadingDropdowns(false));
  }, [isSpecial, domain, companyId]);

  const specialOptions =
    dropdowns.find((d) => d.dropdownName === SPECIAL_KEY_MAP[settingKeyId])?.options || [];

  const handleSave = async () => {
    setSaving(true);
    const loadingToast = toast.loading(existingSetting ? "Updating..." : "Assigning...");

    try {
      let finalValue = value;
      let finalSub = null;

      if (isSpecial) {
        if (!value) {
          toast.dismiss(loadingToast);
          toast.warning("Please select a value");
          setSaving(false);
          return;
        }
        finalValue = value;
      } else if (hasSubValue) {
        finalValue = value;
        finalSub = subValue;
      } else {
        finalValue = enabled ? "1" : "0";
      }

      await assignCompanySetting(domain, {
        companyId,
        settingKeyId: settingKeyId,
        settingValue: finalValue,
        subSettingValue: finalSub,
        settingId: existingSetting?.settingId || null,
      });

      toast.dismiss(loadingToast);
      toast.success(existingSetting ? "Updated" : "Assigned");
      onSaved();
      onClose();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1200,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "8px",
          width: "100%",
          maxWidth: "480px",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            background: THEME.primary,
            padding: "12px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ color: THEME.onPrimary, fontWeight: 600, fontSize: "14px" }}>
            {existingSetting ? "Edit" : "Assign"} — {setting.settingName || setting.SettingName}
          </span>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", color: THEME.onPrimary, fontSize: "18px", cursor: "pointer" }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: "20px 16px" }}>
          {loadingDropdowns ? (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm" style={{ color: THEME.primary }} />
            </div>
          ) : isSpecial ? (
            <div className="mb-3">
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>
                {SPECIAL_KEY_MAP[settingKeyId]
                  ?.replace("extddl", "")
                  ?.replace("extdl", "") || "Value"}
              </label>
              <select
                className="form-select form-select-sm"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                style={{ fontSize: "12px" }}
              >
                <option value="">-- Select --</option>
                {specialOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>
          ) : hasSubValue ? (
            <>
              <div className="mb-3">
                <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>
                  Value
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  style={{ fontSize: "12px" }}
                />
              </div>
              <div className="mb-3">
                <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>
                  Sub Value
                </label>
                <select
                  className="form-select form-select-sm"
                  value={subValue}
                  onChange={(e) => setSubValue(e.target.value)}
                  style={{ fontSize: "12px" }}
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            </>
          ) : (
            <div className="mb-3">
              <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "6px" }}>
                Status
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  style={{ width: "16px", height: "16px" }}
                />
                <span style={{ fontSize: "13px" }}>{enabled ? "Enabled" : "Disabled"}</span>
              </div>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "16px" }}>
            <button
              onClick={onClose}
              className="btn btn-sm btn-outline-secondary"
              style={{ fontSize: "12px" }}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn btn-sm"
              style={{ background: THEME.primary, color: THEME.onPrimary, border: "none", fontSize: "12px" }}
            >
              {saving ? "Saving..." : existingSetting ? "Update" : "Assign"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingAssignModal;